import { useEffect, useRef } from 'react';

import { useFormContext } from '../../hooks/use-form-context';

type ShowIfInnerProps = {
    children: React.ReactNode;
    shouldShow: boolean;
    onShow?: () => void;
    onHide?: () => void;
};

function ShowIfInner({
    children,
    shouldShow,
    onShow,
    onHide,
}: ShowIfInnerProps) {
    const prevShownRef = useRef<boolean | null>(null);

    useEffect(() => {
        // Only trigger callbacks after initial render and when state changes
        if (
            prevShownRef.current !== null
            && prevShownRef.current !== shouldShow
        ) {
            if (shouldShow && onShow) {
                onShow();
            }
            else if (!shouldShow && onHide) {
                onHide();
            }
        }
        prevShownRef.current = shouldShow;
    }, [shouldShow, onShow, onHide]);

    return shouldShow ? <>{children}</> : null;
}

type FormSubscribeSelector = NonNullable<Parameters<ReturnType<typeof useFormContext>['Subscribe']>[0]['selector']>;

export function ShowIf<TSelected>({
    children,
    selector,
    when,
    onShow,
    onHide,
}: {
    children: React.ReactNode;
    selector: (state: Parameters<FormSubscribeSelector>[0]) => TSelected;
    when: (state: TSelected) => boolean;
    onShow?: () => void;
    onHide?: () => void;
}) {
    const form = useFormContext();

    return (
        <form.Subscribe selector={selector}>
            {(value) => {
                const shouldShow = when(value);

                return (
                    <ShowIfInner
                        shouldShow={shouldShow}
                        onShow={onShow}
                        onHide={onHide}
                    >
                        {children}
                    </ShowIfInner>
                );
            }}
        </form.Subscribe>
    );
}
